import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import type { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { Validator } from '@adornis/validation/validation.js';
import type { ValidationFunction } from '@adornis/validation/validator.js';
import { toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { subYears } from 'date-fns';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import type { JobTicketWrapper } from '../job-ticket-wrapper.js';
import { validateJobTicketCancelDate, validateJobTicketOrderDate } from './util-validation.js';
import { isSameMonthValue, toPlainDate } from './jobTicketDates.js';

/**
 * The form document: the plain data of a `JobTicketWrapper`, which is what the functional form
 * fields speak.
 *
 * Partial because an empty form legitimately has no value anywhere yet - what has to be filled in is
 * the validator's business, not the type's.
 */
export type JobTicketFormData = Partial<EntityData<JobTicketWrapper>>;

/** The persisted state a form document is compared against, to skip validation of unchanged dates. */
export interface PersistedJobTicketDates {
  orderDate: Maybe<Date>;
  cancelDate: Maybe<Date>;
}

/** minimum age for a job ticket, mirrors the `validation_birthday_12` message */
const MINIMUM_AGE_IN_YEARS = 12;

type JobTicketValidation<T> = ValidationFunction<Maybe<T>, JobTicketFormData>;

const nonOptionalDate: JobTicketValidation<Date> = options => {
  if (options.value) return;
  throw new ValidationError('', {
    key: options.key,
    translationKey: 'validation_non_optional',
    path: options.path,
  });
};

const birthday: JobTicketValidation<Date> = options => {
  nonOptionalDate(options);
  const birthDay = toPlainDate(options.value);
  if (!birthDay) return;

  // canonical days, so the cutoff is built on the UTC grid - local `subYears` drifts by an hour
  // across a DST boundary and would reject a birthday that is exactly on the limit
  const today = todayAsCalendarDay();
  const earliestAllowedBirthday = toCalendarDay(subYears(today, MINIMUM_AGE_IN_YEARS));

  if (birthDay > earliestAllowedBirthday)
    throw new ValidationError('', {
      key: options.key,
      translationKey: 'validation_birthday_12',
      path: options.path,
    });
};

/**
 * Validator for the job ticket form. Mirrors the `@validate` decorators of {@link JobTicketWrapper},
 * which only run against entity instances and are therefore not reachable from the functional form.
 *
 * @param persisted the currently stored order/cancel dates. Dates that did not change are not
 *   revalidated - a ticket ordered months ago must not block saving unrelated changes today.
 */
export const buildJobTicketValidator = (persisted: PersistedJobTicketDates): Validator => ({
  vorname: sgNonOptional({ allowFalsy: false }),
  nachname: sgNonOptional(),
  geschlecht: sgNonOptional(),
  streetAndNumber: sgNonOptional(),
  zip: sgNonOptional(),
  city: sgNonOptional(),
  country: sgNonOptional(),
  jobTicketKenntnis: sgNonOptional(),
  jobTicketBestaetigt: sgNonOptional(),
  jobTicketDatenweitergabe: sgNonOptional(),

  geburtsDatum: birthday,

  jobTicketBestellDatum: (options => {
    nonOptionalDate(options);
    /** the proxied form value has to be copied before it is compared or stored */
    const orderDate = toPlainDate(options.value);
    if (!orderDate) return;
    /** unchanged order dates stay valid, even if they are in the past by now */
    if (isSameMonthValue(orderDate, persisted.orderDate)) return;

    validateJobTicketOrderDate(new Date(), orderDate, options.key);
  }) satisfies JobTicketValidation<Date>,

  jobTicketKuendigungsDatum: (options => {
    const cancelDate = toPlainDate(options.value);
    if (!cancelDate) return;
    /** unchanged cancel dates stay valid, same reasoning as for the order date */
    if (isSameMonthValue(cancelDate, persisted.cancelDate)) return;

    const orderDate = toPlainDate(options.target.jobTicketBestellDatum);
    validateJobTicketCancelDate(new Date(), orderDate, cancelDate, options.key);
  }) satisfies JobTicketValidation<Date>,
});

/**
 * The job ticket validators carry their message in a translation key (the server has no translator),
 * so form field errors and error dialogs have to resolve it before displaying it.
 */
export const translateJobTicketError = (translationKeyOrMessage: string): string => {
  if (!translationKeyOrMessage) return '';

  const translated: unknown = clientTranslator.translate(translationKeyOrMessage);
  return typeof translated === 'string' ? translated : translationKeyOrMessage;
};

/** The bits of a validation error a message can be built from. */
interface DisplayableError {
  message: string;
  extensions?: { translationKey?: string };
}

/**
 * All human readable messages of a (possibly aggregated) validation error. Errors that carry their
 * own message (e.g. `sgNonOptional`, which names the field) keep it, the rest is translated.
 */
export const collectValidationMessages = (error: ValidationError | MultipleValidationError): string[] => {
  const errors: DisplayableError[] = error.extensions?.validationErrors ?? [error];

  return errors.map(single => single.message || translateJobTicketError(single.extensions?.translationKey ?? ''));
};
